import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { httpClient } from '@/utils/http/client'
import type {
  Conversation,
  ChatMessage,
  CreateConversationResponse,
  ListConversationsResponse,
  SendMessageResponse,
} from '@/types/chat'
import type { AxiosError } from 'axios'

export const useChatStore = defineStore('chat', () => {
  // State
  const conversations = ref<Conversation[]>([])
  const activeConversationId = ref<string | null>(null)
  const messages = ref<Map<string, ChatMessage[]>>(new Map())
  const streamingContent = ref('')
  const streamingMessageId = ref<string | null>(null)
  const isStreaming = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  let accountEpoch = 0

  // Getters
  const activeConversation = computed(() => {
    if (!activeConversationId.value) return null
    return conversations.value.find((c) => c.id === activeConversationId.value) || null
  })

  const activeMessages = computed(() => {
    if (!activeConversationId.value) return []
    return messages.value.get(activeConversationId.value) || []
  })

  const sortedConversations = computed(() => {
    return [...conversations.value].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
  })

  // Actions
  async function fetchConversations(): Promise<void> {
    const epoch = accountEpoch
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.get<ListConversationsResponse>('/chat/conversations')
      if (epoch !== accountEpoch) return

      conversations.value = response.data.conversations
    } catch (err) {
      if (epoch !== accountEpoch) return

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to load conversations'
      throw err
    } finally {
      if (epoch === accountEpoch) {
        loading.value = false
      }
    }
  }

  async function createConversation(title?: string): Promise<void> {
    const epoch = accountEpoch
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.post<CreateConversationResponse>(
        '/chat/conversations',
        title ? { title } : {},
      )
      if (epoch !== accountEpoch) return

      const newConversation: Conversation = {
        id: response.data.id,
        title: response.data.title,
        createdAt: response.data.createdAt,
        updatedAt: response.data.createdAt,
      }

      conversations.value.push(newConversation)
      messages.value.set(newConversation.id, [])
      activeConversationId.value = newConversation.id
    } catch (err) {
      if (epoch !== accountEpoch) return

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to create conversation'
      throw err
    } finally {
      if (epoch === accountEpoch) {
        loading.value = false
      }
    }
  }

  function selectConversation(id: string): void {
    activeConversationId.value = id
  }

  async function deleteConversation(id: string): Promise<void> {
    const epoch = accountEpoch
    loading.value = true
    error.value = null

    try {
      await httpClient.delete(`/chat/conversations/${id}`)
      if (epoch !== accountEpoch) return

      conversations.value = conversations.value.filter((c) => c.id !== id)
      messages.value.delete(id)

      if (activeConversationId.value === id) {
        activeConversationId.value = null
      }
    } catch (err) {
      if (epoch !== accountEpoch) return

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to delete conversation'
      throw err
    } finally {
      if (epoch === accountEpoch) {
        loading.value = false
      }
    }
  }

  async function sendMessage(content: string): Promise<void> {
    if (!activeConversationId.value) return

    const epoch = accountEpoch
    const conversationId = activeConversationId.value
    error.value = null

    // Add user message to the local messages list
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    }

    const currentMessages = messages.value.get(conversationId) || []
    messages.value.set(conversationId, [...currentMessages, userMessage])

    try {
      await httpClient.post<SendMessageResponse>(
        `/chat/conversations/${conversationId}/messages`,
        { content },
      )
      if (epoch !== accountEpoch) return

      isStreaming.value = true
      streamingContent.value = ''
    } catch (err) {
      if (epoch !== accountEpoch) return

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to send message'
      isStreaming.value = false
      streamingContent.value = ''
      throw err
    }
  }

  function handleStreamToken(content: string): void {
    streamingContent.value += content
  }

  function handleStreamDone(messageId: string): void {
    if (!activeConversationId.value) return

    const conversationId = activeConversationId.value

    const assistantMessage: ChatMessage = {
      id: messageId,
      role: 'assistant',
      content: streamingContent.value,
      createdAt: new Date().toISOString(),
    }

    const currentMessages = messages.value.get(conversationId) || []
    messages.value.set(conversationId, [...currentMessages, assistantMessage])

    streamingContent.value = ''
    streamingMessageId.value = null
    isStreaming.value = false
  }

  function handleStreamError(message: string): void {
    error.value = message
    streamingContent.value = ''
    streamingMessageId.value = null
    isStreaming.value = false
  }

  function clearError(): void {
    error.value = null
  }

  function resetAccountState(): void {
    accountEpoch++
    conversations.value = []
    activeConversationId.value = null
    messages.value = new Map()
    streamingContent.value = ''
    streamingMessageId.value = null
    isStreaming.value = false
    loading.value = false
    error.value = null
  }

  return {
    // State
    conversations,
    activeConversationId,
    messages,
    streamingContent,
    streamingMessageId,
    isStreaming,
    loading,
    error,
    // Getters
    activeConversation,
    activeMessages,
    sortedConversations,
    // Actions
    fetchConversations,
    createConversation,
    selectConversation,
    deleteConversation,
    sendMessage,
    handleStreamToken,
    handleStreamDone,
    handleStreamError,
    clearError,
    resetAccountState,
  }
})
